import React, { useRef, useState, useEffect } from 'react';
import { getSurveys, getTemplateByType, getDynamicQuestions, getCategoryForAge, getCategoryName, getMemberAge, getMemberName, getMemberPhoto, getMemberPhone } from '../services/storage';
import { CardField, SurveyData, DynamicQuestion, User, UserRole } from '../types';
import { drawCardOnCanvas, downloadCanvas, formatSerialNoForDisplay, AVAILABLE_FIELDS } from '../utils/cardUtils';
import { Loader, Download, Search, AlertCircle, FileArchive, Filter, Calendar } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface IdCardPrinterProps {
  currentUser?: User;
}

const IdCardPrinter: React.FC<IdCardPrinterProps> = ({ currentUser }) => {
  const isViewOnly = currentUser?.role === UserRole.VIEW_ONLY_ADMIN;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null);
  const [templateImg, setTemplateImg] = useState<string | null>(null);
  const [fields, setFields] = useState<CardField[]>([]);
  const [photoConfig, setPhotoConfig] = useState<any>(undefined);
  const [surveys, setSurveys] = useState<SurveyData[]>([]);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('Paid'); // Default to Paid
  
  const [isZipping, setIsZipping] = useState(false);
  
  // Flattened list of students
  const [students, setStudents] = useState<any[]>([]);
  const [selectedStudentIndex, setSelectedStudentIndex] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [savedTemplate, savedSurveys, fetchedQuestions] = await Promise.all([
          getTemplateByType('student'), 
          getSurveys({ limitCount: 1000 }), // fetch a large amount to ensure we cover the dates
          getDynamicQuestions()
        ]);
        
        if (savedTemplate) {
          setTemplateImg(savedTemplate.backgroundImage);
          setFields(savedTemplate.fields);
          setPhotoConfig(savedTemplate.photoConfig);
        }
        setSurveys(savedSurveys);
        setQuestions(fetchedQuestions);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (surveys.length > 0 && questions.length > 0) {
      const allStudents: any[] = [];
      
      // Check if a member is a parent, spouse, or non-student adult
      const isAdultOrNonStudent = (member: any): boolean => {
        if (!member || typeof member !== 'object') return true;
        for (const key of Object.keys(member)) {
          const val = member[key];
          if (typeof val === 'string' && val.trim().length > 0) {
            const v = val.toLowerCase().trim();
            const isParentOrSpouseWord = 
              v.includes('father') || v.includes('mother') || v.includes('parent') || v.includes('guardian') ||
              v.includes('spouse') || v.includes('wife') || v.includes('husband') || v.includes('self') || v.includes('head') ||
              v.includes('പിതാവ്') || v.includes('പിതാ') || v.includes('അച്ഛൻ') || v.includes('ഉപ്പ') ||
              v.includes('മാതാവ്') || v.includes('മാതാ') || v.includes('അമ്മ') || v.includes('ഉമ്മ') ||
              v.includes('ഭാര്യ') || v.includes('ഭർത്താവ്') || v.includes('രക്ഷിതാവ്') || v.includes('രക്ഷിതാ') ||
              v.includes('രക്ഷിതാവിന്റെ') || v.includes('നാഥൻ') || v.includes('നാഥ') || v.includes('സ്വയം');
              
            if (isParentOrSpouseWord) {
              const k = key.toLowerCase();
              if (
                k.includes('relation') || k.includes('ബന്ധം') || k.includes('head') || 
                k.includes('status') || k.includes('role') || k.includes('type') ||
                v === 'father' || v === 'mother' || v === 'spouse' || v === 'wife' || v === 'husband' || v === 'self' || v === 'head' ||
                v === 'പിതാവ്' || v === 'മാതാവ്' || v === 'ഭാര്യ' || v === 'ഭർത്താവ്' || v === 'സ്വയം' || v === 'നാഥൻ'
              ) {
                return true;
              }
            }
          }
        }
        return false;
      };

      surveys.forEach(survey => {
        // Extract flat data
        const flatData = Object.keys(survey).reduce<Record<string, any>>((acc, key) => {
          if (['id', 'submittedBy', 'submittedAt', 'paymentStatus', 'paymentAmount', 'paidBy', 'paidAt'].includes(key)) return acc;
          const val = (survey as any)[key];
          if (Array.isArray(val)) return acc; // Skip repeaters
          if (typeof val === 'object' && val !== null) {
            return { ...acc, ...val };
          }
          return { ...acc, [key]: val };
        }, {});

        const name = getMemberName(flatData, questions);
        
        // Process primary student under strict criteria
        if (name) {
          const isPrimaryAdult = isAdultOrNonStudent(flatData);
          const primaryAge = getMemberAge(flatData, questions);
          const primaryCategory = getCategoryForAge(primaryAge);

          const primaryNameLower = name.toLowerCase();
          const primaryIsHobbyWord = primaryNameLower.includes('hobby') || primaryNameLower.includes('hobbies') || primaryNameLower.includes('വിനോദം') || primaryNameLower.includes('sports') || primaryNameLower.includes('art') || primaryNameLower.includes('football') || primaryNameLower.includes('cricket') || primaryNameLower.includes('reading') || primaryNameLower.includes('drawing');
          const isPrimaryHobby = primaryIsHobbyWord && !primaryNameLower.includes('name') && !primaryNameLower.includes('പേര്');

          if (!isPrimaryAdult && primaryCategory && !isPrimaryHobby) {
            allStudents.push({ 
              ...flatData, 
              id: survey.id + '-main',
              studentName: name,
              serialNo: flatData.studentSerialNo || flatData.serialNo || flatData.idNo || 'PND',
              surveyRef: survey
            });
          }
        }

        // Check repeaters
        const repeaterKeys = Object.keys(survey).filter(key => Array.isArray((survey as any)[key]));
        let repeaterMembers: any[] = [];
        for (const key of repeaterKeys) {
          repeaterMembers = [...repeaterMembers, ...((survey as any)[key] || [])];
        }
        
        repeaterMembers.forEach((m: any, idx: number) => {
          const name = getMemberName(m, questions);
          if (name) {
            if (isAdultOrNonStudent(m)) return;

            const age = getMemberAge(m, questions);
            const category = getCategoryForAge(age);
            if (!category) return;

            const nameLower = name.toLowerCase();
            const isHobbyWord = nameLower.includes('hobby') || nameLower.includes('hobbies') || nameLower.includes('വിനോദം') || nameLower.includes('sports') || nameLower.includes('art') || nameLower.includes('football') || nameLower.includes('cricket') || nameLower.includes('reading') || nameLower.includes('drawing');
            if (isHobbyWord && !nameLower.includes('name') && !nameLower.includes('പേര്')) return;

            allStudents.push({
              ...flatData, // Merge family-level data first
              ...m, // Override with specific member data
              id: survey.id + '-rep-' + idx,
              studentName: name,
              serialNo: m.serialNo || m.idNo || 'PND',
              surveyRef: survey
            });
          }
        });
      });
      
      setStudents(allStudents);
    }
  }, [surveys, questions]);

  const filteredStudents = students.filter(s => {
    // 1. Payment Status Filter
    const sStatus = s.surveyRef.paymentStatus || 'Pending';
    if (paymentStatus !== 'All' && sStatus !== paymentStatus) return false;

    // 2. Date Filter
    if (startDate || endDate) {
      const surveyDate = new Date(s.surveyRef.submittedAt || new Date());
      if (startDate && surveyDate < new Date(startDate)) return false;
      if (endDate) {
        const end = new Date(endDate);
        if (endDate.length <= 10) {
          end.setHours(23, 59, 59, 999);
        }
        if (surveyDate > end) return false;
      }
    }

    // 3. Search Query
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const name = String(s.studentName || getMemberName(s, questions)).toLowerCase();
    const serial = String(s.serialNo || '').toLowerCase();
    const receiptId = String(s.surveyRef.id || '').toLowerCase();
    return name.includes(q) || serial.includes(q) || receiptId.includes(q);
  });

  const selectedStudent = filteredStudents[selectedStudentIndex] || null;

  useEffect(() => {
    if (templateImg && canvasRef.current && selectedStudent) {
      let previewData = {};
      
      const member = selectedStudent;
      const age = getMemberAge(member, questions);
      const name = member.studentName || getMemberName(member, questions);
      const phone = getMemberPhone(member, questions) || getMemberPhone(member.surveyRef, questions);
      const photo = getMemberPhoto(member, questions);

      const category = getCategoryForAge(age);
      const categoryName = getCategoryName(category);

      previewData = {
        ...member.surveyRef,
        ...member,
        name,
        headName: name,
        phone,
        housePhoto: photo,
        serialNo: formatSerialNoForDisplay(member.serialNo),
        id: formatSerialNoForDisplay(member.serialNo),
        categoryName
      };
      
      drawCardOnCanvas(canvasRef.current, { backgroundImage: templateImg, fields, photoConfig }, { ...previewData, isEditor: false });
    }
  }, [templateImg, fields, photoConfig, selectedStudent, questions]);

  const handleDownload = () => {
    if (!canvasRef.current || !selectedStudent) return;
    const name = selectedStudent.studentName || getMemberName(selectedStudent, questions) || 'student';
    const cleanName = name.replace(/[^a-zA-Z0-9]/g, '_');
    const serial = formatSerialNoForDisplay(selectedStudent.serialNo);
    downloadCanvas(canvasRef.current, `ID_${serial}_${cleanName}.png`);
  };

  const handleBulkDownload = async () => {
    if (!templateImg || filteredStudents.length === 0 || !hiddenCanvasRef.current) return;
    setIsZipping(true);
    
    try {
      const zip = new JSZip();
      const folder = zip.folder("ID_Cards");
      
      for (const student of filteredStudents) {
        const age = getMemberAge(student, questions);
        const name = student.studentName || getMemberName(student, questions) || 'student';
        const phone = getMemberPhone(student, questions) || getMemberPhone(student.surveyRef, questions);
        const photo = getMemberPhoto(student, questions);
        const category = getCategoryForAge(age);
        const categoryName = getCategoryName(category);
        const serial = formatSerialNoForDisplay(student.serialNo);

        const previewData = {
          ...student.surveyRef,
          ...student,
          name,
          headName: name,
          phone,
          housePhoto: photo,
          serialNo: serial,
          id: serial,
          categoryName
        };

        // Draw on hidden canvas
        await drawCardOnCanvas(hiddenCanvasRef.current, { backgroundImage: templateImg, fields, photoConfig }, { ...previewData, isEditor: false });
        
        // Convert to blob
        const blob = await new Promise<Blob | null>(resolve => hiddenCanvasRef.current?.toBlob(resolve, 'image/png'));
        
        if (blob && folder) {
          const cleanName = name.replace(/[^a-zA-Z0-9]/g, '_');
          folder.file(`ID_${serial}_${cleanName}.png`, blob);
        }
      }
      
      const zipContent = await zip.generateAsync({ type: 'blob' });
      saveAs(zipContent, `ID_Cards_Export.zip`);
    } catch (e) {
      console.error('Error creating ZIP:', e);
      alert('Failed to generate ZIP file.');
    } finally {
      setIsZipping(false);
    }
  };

  if (loading) return <div className="p-10 text-center"><Loader className="animate-spin inline mr-2"/> Loading...</div>;

  return (
    <div className="flex flex-col lg:flex-row gap-6 pb-24 lg:pb-10 h-full">
      {/* Hidden Canvas for Bulk Download */}
      <canvas ref={hiddenCanvasRef} style={{ display: 'none' }} />

      {/* Student Selection Panel */}
      <div className="bg-white p-6 rounded-lg shadow-md space-y-4 lg:w-2/5 flex flex-col h-[calc(100vh-100px)]">
        <h2 className="text-xl font-bold border-b pb-2 text-gray-800 flex justify-between items-center">
          ID Card Printing
          <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{filteredStudents.length} Students</span>
        </h2>
        
        {/* Filters */}
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 space-y-3">
           <div className="flex items-center gap-2 text-sm font-bold text-gray-600 mb-1">
             <Filter size={16} /> Filters
           </div>
           
           <div className="grid grid-cols-2 gap-2">
             <div>
               <label className="text-[10px] uppercase font-black text-gray-500 block mb-1">Payment Status</label>
               <select 
                 value={paymentStatus}
                 onChange={(e) => setPaymentStatus(e.target.value)}
                 className="w-full text-sm border-gray-300 rounded p-1.5 outline-none focus:ring-2 focus:ring-blue-500"
               >
                 <option value="All">All Statuses</option>
                 <option value="Paid">Paid</option>
                 <option value="Pending">Pending</option>
               </select>
             </div>
             <div>
               <label className="text-[10px] uppercase font-black text-gray-500 block mb-1">Search</label>
               <div className="relative">
                 <Search className="absolute left-2 top-1.5 text-gray-400" size={14} />
                 <input
                   type="text"
                   className="w-full text-sm border border-gray-300 rounded pl-7 pr-2 py-1.5 focus:ring-2 focus:ring-blue-500 outline-none"
                   placeholder="Name, receipt no..."
                   value={searchQuery}
                   onChange={(e) => {
                     setSearchQuery(e.target.value);
                     setSelectedStudentIndex(0);
                   }}
                 />
               </div>
             </div>
           </div>

           <div className="grid grid-cols-2 gap-2">
             <div>
               <label className="text-[10px] uppercase font-black text-gray-500 block mb-1">From Date & Time</label>
               <input
                 type="datetime-local"
                 className="w-full text-sm border-gray-300 rounded p-1.5 outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-800"
                 value={startDate}
                 onChange={(e) => setStartDate(e.target.value)}
               />
             </div>
             <div>
               <label className="text-[10px] uppercase font-black text-gray-500 block mb-1">To Date & Time</label>
               <input
                 type="datetime-local"
                 className="w-full text-sm border-gray-300 rounded p-1.5 outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-800"
                 value={endDate}
                 onChange={(e) => setEndDate(e.target.value)}
               />
             </div>
           </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 pr-2">
          {filteredStudents.length === 0 ? (
            <p className="text-center text-gray-500 italic mt-10">No students found.</p>
          ) : (
            filteredStudents.map((student, idx) => {
              const name = student.studentName || getMemberName(student, questions);
              const age = getMemberAge(student, questions);
              const cat = getCategoryForAge(age);
              const isPaid = student.surveyRef.paymentStatus === 'Paid';
              
              return (
                <div
                  key={student.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedStudentIndex === idx 
                      ? 'bg-blue-50 border-blue-400 ring-1 ring-blue-400' 
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedStudentIndex(idx)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-gray-800 uppercase text-sm leading-tight">{name || 'Unnamed'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[10px] text-gray-500 font-bold uppercase">
                          <span className="text-emerald-700">{getCategoryName(cat)}</span>
                        </p>
                        <span className="text-gray-300">|</span>
                        <p className={`text-[10px] font-black uppercase ${isPaid ? 'text-emerald-600' : 'text-amber-500'}`}>
                          {isPaid ? 'PAID' : 'PENDING'}
                        </p>
                      </div>
                      <p className="text-[9px] text-gray-400 font-mono mt-0.5" title="Receipt No">
                        Receipt: {student.surveyRef.id}
                      </p>
                    </div>
                    <div className="bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded font-black whitespace-nowrap">
                      {formatSerialNoForDisplay(student.serialNo)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Preview Panel */}
      <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center lg:w-3/5 h-[calc(100vh-100px)]">
        <div className="w-full flex justify-between items-center mb-4 border-b pb-2">
            <h2 className="text-xl font-bold text-gray-800">Card Preview</h2>
            {!isViewOnly && (
              <div className="flex gap-2">
                <button 
                  onClick={handleDownload}
                  disabled={!selectedStudent || !templateImg}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 rounded-lg font-bold flex items-center transition-colors text-sm"
                >
                  <Download size={16} className="mr-1.5" />
                  Single
                </button>
                <button 
                  onClick={handleBulkDownload}
                  disabled={!templateImg || filteredStudents.length === 0 || isZipping}
                  className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg font-bold flex items-center transition-colors text-sm"
                >
                  {isZipping ? <Loader size={16} className="mr-1.5 animate-spin" /> : <FileArchive size={16} className="mr-1.5" />}
                  {isZipping ? 'Zipping...' : `Download All (${filteredStudents.length})`}
                </button>
              </div>
            )}
        </div>

        <div className="relative border-2 border-gray-200 border-dashed shadow-sm rounded-lg w-full bg-gray-50 overflow-auto flex-1 flex flex-col items-center justify-center p-4">
             {!templateImg ? (
                <div className="text-center p-10">
                    <p className="text-gray-400 italic mb-2">No template configured.</p>
                    <p className="text-xs text-gray-400">Please setup the template in 'ID Card Mgmt' first.</p>
                </div>
             ) : !selectedStudent ? (
                <div className="text-center p-10">
                    <p className="text-gray-400 italic mb-2">No student selected.</p>
                    <p className="text-xs text-gray-400">Please select a student from the list.</p>
                </div>
             ) : (
                <div className="inline-block relative">
                   <canvas 
                     ref={canvasRef} 
                     className="max-w-none h-auto block shadow-lg"
                     style={{ maxWidth: '100%', userSelect: 'none' }}
                   />
                </div>
             )}
        </div>
      </div>
    </div>
  );
};

export default IdCardPrinter;

